// src/pages/Blog.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FALLBACK_POSTS } from '../data/blogData';

import blog1Img from '../assets/images/blog1.jpg';

function normalizePost(d) {
  const data = d.data ? d.data() : d;
  const id = d.id || data.slug;
  return {
    id,
    slug: data.slug,
    title: data.title,
    excerpt: data.excerpt,
    category: data.category,
    img: data.imageUrl || data.img || '',
    date: data.date || (data.publishedAt?.toDate
      ? data.publishedAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : ''),
    content: data.content || '',
    metaTitle: data.metaTitle,
    metaDescription: data.metaDescription,
    metaKeywords: data.metaKeywords,
  };
}

// Keep exporting BLOG_POSTS as alias for backward compat with any imports
export const BLOG_POSTS = FALLBACK_POSTS;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const q = query(collection(db, 'blog_posts'), orderBy('publishedAt', 'desc'));
        const snap = await getDocs(q);
        const fetched = snap.docs.map(normalizePost);
        setPosts(fetched.length > 0 ? fetched : FALLBACK_POSTS);
      } catch {
        setPosts(FALLBACK_POSTS);
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, []);

  return (
    <>
      <SEO
        title="Insights"
        description="IT and workforce insights from District Sourcing Tech — cloud cost management, system modernization, workforce continuity and more."
        url="https://districtsourcingtech.com/blog"
        keywords="IT insights, cloud costs, system modernization, workforce continuity, government IT blog"
      />
      <Navbar variant="light" />

      {/* Hero - career-hero pattern */}
      <section className="career-hero">
        <div className="career-hero__bg">
          <img src={blog1Img} alt="Insights and blog" />
        </div>
        <div className="career-hero__overlay" />
        <div className="career-hero__card">
          <p className="career-hero__label">Insights</p>
          <h1 className="title-1" style={{ marginBottom: '12px', marginTop: '4px' }}>Our blog</h1>
          <p className="body-lg" style={{ color: 'var(--token-desc)', marginBottom: '32px' }}>
            Perspectives on IT operations, workforce strategy and government technology delivery.
          </p>
          <Link to="#posts" className="btn btn-primary">Browse all</Link>
        </div>
      </section>

      <section id="posts" className="section">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--token-desc)' }}>
              <span className="spinner" style={{ borderColor: 'rgba(0,0,0,0.1)', borderTopColor: 'var(--primary-500)', margin: '0 auto 16px', display: 'block' }} />
              Loading insights...
            </div>
          ) : (
            <div className="blog-grid-new">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="blog-card-new"
                >
                  {post.img && (
                    <div className="blog-card-new__image">
                      <img src={post.img} alt={post.title} loading="lazy" />
                    </div>
                  )}
                  <div className="blog-card-new__body">
                    <span className="body-sm" style={{ color: 'var(--primary-500)' }}>
                      {post.category}
                    </span>
                    <h2 className="title-5">{post.title}</h2>
                    <p className="body-lg" style={{ color: 'var(--token-desc)', marginTop: '4px', flex: 1 }}>
                      {post.excerpt}
                    </p>
                  </div>
                  <div className="blog-card-new__footer">
                    <span className="body-sm" style={{ color: 'var(--token-desc)' }}>{post.date}</span>
                    <span className="read-more">
                      Read more{' '}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}